import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, filter, finalize, take } from 'rxjs/operators';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  DeviceAdmin,
  GetAllDevices,
  GetDeviceByIdResponse,
} from 'src/app/core/models/admin';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../../../models/common/api.common.model';
import { CommonUtils } from '../../../utils/common/common.utils';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  baseUrl = environment.apiUrl.concat('device-management/');

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorhandleService,
    private commonUtils: CommonUtils,
  ) {}

  /**
   * Get all devices by location
   *
   * @param {string} _locationCode
   * @return {*}  {Observable<GetAllDevices>}
   * @memberof DeviceService
   */
  getAllDevicesByLocation(locationCode: string): Observable<GetAllDevices> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetAllDevices>(this.baseUrl.concat('get-all-devices-by-location'), {
        params: { locationCode: locationCode },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetAllDevices>(
            error,
            error.status,
            'Device Service: Get All Devices',
            BANNER_TYPE.TABLE_DATA_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Add a device by location
   *
   * @param {DeviceAdmin} device
   * @return {*}  {Observable<any>}
   * @memberof DeviceService
   */
  addDeviceByLocation(body: DeviceAdmin): Observable<ApiResponse | unknown> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post(this.baseUrl.concat('add-device-by-location'), { device: body })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<unknown>(
            error,
            error.status,
            'Device Service: Add Device By Location',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Edit a device by location
   *
   * @param {string} locationCode
   * @param {number} serialNumber
   * @param {DeviceAdmin} body
   * @return {*}  {Observable<any>}
   * @memberof DeviceService
   */
  editDeviceByLocation(body: DeviceAdmin): Observable<ApiResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .post<ApiResponse>(this.baseUrl.concat('edit-device-in-location'), {
        device: body,
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ApiResponse>(
            error,
            error.status,
            'Device Service: Edit Device By Location',
            BANNER_TYPE.API_400_ERROR,
            false as unknown as ApiResponse,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get Device by Id
   *
   * @param {string} deleteDeviceId
   * @return {*}  {Observable<any>}
   * @memberof DeviceService
   */
  getDeviceById(deviceId: number): Observable<GetDeviceByIdResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<GetDeviceByIdResponse>(this.baseUrl.concat(`get-device/${deviceId}`))
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<GetDeviceByIdResponse>(
            error,
            error.status,
            'Device Service: Get Device By Id',
            BANNER_TYPE.API_400_ERROR,
            false as unknown as GetDeviceByIdResponse,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Delete a Device
   *
   * @param {string} deleteDeviceId
   * @return {*}  {Observable<any>}
   * @memberof DeviceService
   */
  deleteDevice(deleteDeviceId: string): Observable<ApiResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .delete<ApiResponse>(this.baseUrl.concat('delete-device'), {
        params: { deleteDeviceId: deleteDeviceId },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ApiResponse>(
            error,
            error.status,
            'Device Service: Delete Device',
            BANNER_TYPE.API_400_ERROR,
            false as unknown as ApiResponse,
          ),
        ),
        filter((value) => !!value),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
